<script setup lang="ts">
  import { genericErrorToast } from '@/lib/toasts'
  import { useTranslation } from '@/composables/useTranslation'
  import { AccountsService, RoleEnum, type CustomerCalendar, type TUser } from '@/types/generated'
  import { computed, ref, watch } from 'vue'
  import CalendarProviderLogo from './CalendarProviderLogo.vue'
  import type { ProviderKey } from './CalendarProviderLogo.vue'
  import DisconnectWarningModal from './settings/DisconnectWarningModal.vue'

  const props = withDefaults(
    defineProps<{
      calendars: CustomerCalendar[]
      calendarKindChecks?: Record<string, Record<EntryKind, boolean>>
      currentUser?: TUser | null
      disconnectingAccountUuid?: string | null
      accountsLoading?: boolean
      restoreModalOnReturn?: boolean
    }>(),
    {
      currentUser: null,
      disconnectingAccountUuid: null,
      accountsLoading: false,
      restoreModalOnReturn: false,
      calendarKindChecks: () => ({})
    }
  )

  const emit = defineEmits<{
    'disconnect-account': [accountUuid: string]
    'set-account-kind-sync': [
      payload: { calendarUuids: string[]; kind: EntryKind; checked: boolean }
    ]
  }>()

  const { t, locale } = useTranslation({
    de: {
      syncTab: 'Synchronisation',
      permissionsTab: 'Berechtigungen',
      appointments: 'Termine',
      deadlines: 'Fristen',
      followUps: 'Wiedervorlagen',
      sourceCalendar: 'Über Kalender verbunden',
      sourceBooking: 'Über Terminbuchung verbunden',
      sourceBoth: 'Verbunden in Kalender & Terminbuchung',
      statusSynced: 'Synchronisiert',
      statusSyncing: 'Wird synchronisiert',
      statusError: 'Fehler bei der Synchronisation',
      owner: 'Eigentümer:in',
      edit: 'Bearbeiten',
      view: 'Nur Ansehen',
      currentUser: 'Aktueller Nutzer',
      timeUnknown: 'Zeitpunkt unbekannt',
      viewModeTitle: 'Demo-Umschalter: zeigt, was Admins gegenüber Mitarbeitenden sehen.',
      viewLabel: 'Sicht:',
      admin: 'Admin',
      staff: 'Mitarbeiter:in',
      back: 'Zurück',
      addCalendarAccount: 'Kalender-Konto hinzufügen',
      chooseProviderDescription:
        'Wählen Sie einen Anbieter — die Anmeldung erfolgt sicher beim Anbieter selbst.',
      includesService: 'Inkl. {service}',
      requiredPermissionsHelp:
        'Wir fragen nur die für die Terminplanung nötigen Kalenderrechte (Lesen & Schreiben) ab.',
      redirectToProvider: 'Weiter zu {provider}',
      redirectToProviderDescription:
        'Sie werden jetzt zur sicheren Anmeldung und Freigabe direkt bei {provider} weitergeleitet.',
      providerAuthHappensExternally:
        'Anmeldung und Freigabe passieren im Browser direkt bei {provider}, nicht in Jupus.',
      accessRequestTitle: 'Jupus möchte zugreifen auf:',
      accessCalendarPermission: '· Kalender lesen & schreiben (Termine, Fristen, Wiedervorlagen)',
      accessVideoService: '· {service}-Konferenzen erstellen',
      accessAccountEmail: '· E-Mail-Adresse Ihres Kontos',
      continue: 'Weiter',
      connectionFailed: 'Verbindung fehlgeschlagen',
      backToSelection: 'Zur Auswahl',
      retry: 'Erneut versuchen',
      noAccountsSynced: 'Keine Konten synchronisiert',
      syncEmptyDescription: 'Verbinde einen Kalender, um deine Termine zu synchronisieren.',
      permissionsEmptyDescription: 'Verbinde einen Kalender, um Berechtigungen zu verwalten.',
      syncCalendar: 'Kalender synchronisieren',
      accountsApplyHeadline: 'Konten gelten für Kalender & Terminbuchung',
      accountsApplyDescription:
        'Einmal verbinden, in beiden Features verfügbar. Neue Konten erscheinen automatisch in der Terminbuchung – und umgekehrt.',
      connectedAccounts: 'Verbundene Konten',
      addAccount: 'Konto hinzufügen',
      videoConferencesVia: 'Videokonferenzen via {service}',
      disconnectConnection: 'Verbindung trennen',
      readOnlyAccess: 'Nur Lesezugriff',
      readOnlyDisconnectHint: 'Nur der Eigentümer oder ein Admin kann diese Verbindung trennen.',
      retryConnection: 'Verbindung erneuern',
      syncNeedsAttention:
        'Die Verbindung braucht erneut Ihre Bestätigung beim Anbieter, damit die Synchronisation weiterläuft.',
      entryKind: 'Eintragsart',
      syncColumn: 'Synchronisieren',
      limitedManagementTitle: 'Eingeschränkte Verwaltung',
      limitedManagementDescription:
        'Sie sehen nur Kalender, die Sie besitzen oder zu denen Sie eingeladen wurden. Berechtigungen können Sie nur für Ihren eigenen Kalender verwalten.',
      removeAccess: 'Zugriff entfernen',
      invitePerson: '+ Person einladen',
      nameOrEmailPlaceholder: 'Name oder E-Mail …',
      cancel: 'Abbrechen',
      inviteEmail: 'Einladen – {email}',
      disconnectConfirmTitle: 'Konto wirklich trennen?',
      disconnectConfirmDescription: '{email} wird aus Kalender und Terminbuchung entfernt.',
      affectedServicesTitle: 'Achtung: Buchbare Leistungen betroffen',
      affectedServicesDescription:
        'Folgende Leistungen werden nach dem Trennen nicht mehr buchbar sein: {services}',
      disconnect: 'Trennen',
      affectedServicesEmpty: 'Keine aktiven Leistungen sind von diesem Trennen betroffen.',
      affectedServicesLoading: 'Betroffene Leistungen werden geladen …',
      affectedServicesLoadError:
        'Die betroffenen Leistungen konnten gerade nicht geladen werden. Bitte versuchen Sie es erneut.',
      duplicateAccountError:
        'Das Konto {email} ist bereits mit {provider} verbunden. Bitte wählen Sie ein anderes Konto.',
      loadingAccounts: 'Verbundene Konten werden geladen …'
    },
    en: {
      syncTab: 'Sync',
      permissionsTab: 'Permissions',
      appointments: 'Appointments',
      deadlines: 'Deadlines',
      followUps: 'Follow-ups',
      sourceCalendar: 'Connected via calendar',
      sourceBooking: 'Connected via appointment booking',
      sourceBoth: 'Connected in calendar & appointment booking',
      statusSynced: 'Synced',
      statusSyncing: 'Syncing',
      statusError: 'Sync error',
      owner: 'Owner',
      edit: 'Edit',
      view: 'View only',
      currentUser: 'Current user',
      timeUnknown: 'Time unknown',
      viewModeTitle: 'Demo switch: shows what admins see compared with staff.',
      viewLabel: 'View:',
      admin: 'Admin',
      staff: 'Staff',
      back: 'Back',
      addCalendarAccount: 'Add calendar account',
      chooseProviderDescription:
        'Choose a provider — sign-in happens securely with the provider itself.',
      includesService: 'Incl. {service}',
      requiredPermissionsHelp:
        'We only request the calendar permissions needed for scheduling (read & write).',
      redirectToProvider: 'Continue to {provider}',
      redirectToProviderDescription:
        'You will now be redirected to {provider} to sign in securely and grant access.',
      providerAuthHappensExternally:
        'Sign-in and permission approval happen in your browser directly with {provider}, not in Jupus.',
      accessRequestTitle: 'Jupus wants access to:',
      accessCalendarPermission: '· Read & write calendars (appointments, deadlines, follow-ups)',
      accessVideoService: '· Create {service} meetings',
      accessAccountEmail: '· Your account email address',
      continue: 'Continue',
      connectionFailed: 'Connection failed',
      backToSelection: 'Back to selection',
      retry: 'Try again',
      noAccountsSynced: 'No accounts synced',
      syncEmptyDescription: 'Connect a calendar to sync your appointments.',
      permissionsEmptyDescription: 'Connect a calendar to manage permissions.',
      syncCalendar: 'Sync calendar',
      accountsApplyHeadline: 'Accounts apply to calendar & appointment booking',
      accountsApplyDescription:
        'Connect once, use in both features. New accounts appear automatically in appointment booking — and vice versa.',
      connectedAccounts: 'Connected accounts',
      addAccount: 'Add account',
      videoConferencesVia: 'Video conferences via {service}',
      disconnectConnection: 'Disconnect',
      readOnlyAccess: 'Read-only access',
      readOnlyDisconnectHint: 'Only the owner or an admin can disconnect this connection.',
      retryConnection: 'Reconnect',
      syncNeedsAttention:
        'This connection needs to be confirmed again with the provider so sync can continue.',
      entryKind: 'Entry type',
      syncColumn: 'Sync',
      limitedManagementTitle: 'Limited management',
      limitedManagementDescription:
        'You only see calendars you own or were invited to. You can only manage permissions for your own calendar.',
      removeAccess: 'Remove access',
      invitePerson: '+ Invite person',
      nameOrEmailPlaceholder: 'Name or email …',
      cancel: 'Cancel',
      inviteEmail: 'Invite – {email}',
      disconnectConfirmTitle: 'Really disconnect account?',
      disconnectConfirmDescription:
        '{email} will be removed from calendar and appointment booking.',
      affectedServicesTitle: 'Warning: bookable services affected',
      affectedServicesDescription:
        'The following services will no longer be bookable after disconnecting: {services}',
      disconnect: 'Disconnect',
      affectedServicesEmpty: 'No active services are affected by this disconnect.',
      affectedServicesLoading: 'Loading affected services …',
      affectedServicesLoadError:
        'The affected services could not be loaded right now. Please try again.',
      duplicateAccountError:
        'The account {email} is already connected with {provider}. Please choose a different account.',
      loadingAccounts: 'Loading connected accounts …'
    }
  })

  type SettingsTab = 'sync' | 'permissions'
  type Perspective = 'admin' | 'staff'
  type AccountSource = 'calendar' | 'terminbuchung' | 'both'
  type SyncStatus = 'synced' | 'syncing' | 'error'
  type PermissionLevel = 'owner' | 'edit' | 'view'
  type EntryKind = 'termin' | 'frist' | 'wiedervorlage'
  type AddStep = 'closed' | 'choose' | 'confirm' | 'error'
  type OrganisationUser = TUser['org']['users'][number]
  type SettingsUser = OrganisationUser & {
    name: string
    initials: string
  }

  interface PermissionRow {
    userId?: string
    email?: string
    name: string
    initials: string
    level: PermissionLevel
  }

  interface ConnectedAccount {
    id: string
    accountUuid?: string
    profileId?: number
    calendarUuids: string[]
    provider: ProviderKey
    providerInferred: boolean
    email: string
    source: AccountSource
    status: SyncStatus
    lastSyncAt: string | null
    syncedKinds: Record<EntryKind, boolean>
    bookableLeistungen: string[]
    ownerId: string | 'firm'
    calendarName: string
    permissionLevel: string
    canManage: boolean
    retryUrl?: string | null
    errorMessage?: string | null
    localOnly?: boolean
  }

  interface PermissionsWithMeta {
    account: ConnectedAccount
    permissions: PermissionRow[]
    canManage: boolean
    ownerAssignable: boolean
  }

  const ENTRY_KINDS: EntryKind[] = ['termin', 'frist', 'wiedervorlage']
  const tabs = computed<{ id: SettingsTab; label: string }[]>(() => [
    { id: 'sync', label: t('syncTab') },
    { id: 'permissions', label: t('permissionsTab') }
  ])
  interface ProviderMeta {
    label: string
    videoService: string
  }

  const PROVIDER_META: Record<ProviderKey, ProviderMeta> = {
    google: {
      label: 'Google',
      videoService: 'Google Meet'
    },
    icloud: {
      label: 'iCloud',
      videoService: 'FaceTime'
    },
    office365: {
      label: 'Office 365',
      videoService: 'Microsoft Teams'
    },
    exchange: {
      label: 'Microsoft Exchange',
      videoService: 'Microsoft Teams'
    },
    outlook: {
      label: 'Outlook.com',
      videoService: 'Microsoft Teams'
    }
  }

  const providerKeys: ProviderKey[] = ['google', 'icloud', 'office365', 'exchange', 'outlook']

  const activeTab = ref<SettingsTab>('sync')
  const viewAs = ref<Perspective>('staff')
  const syntheticAccounts = ref<ConnectedAccount[]>([])
  // TODO(CAL-031): Persist permissions changes once backend endpoints are available.
  const permissionsState = ref<Record<string, PermissionRow[]>>({})

  const entryKindMeta = (kind: EntryKind): { label: string; color: string } => {
    switch (kind) {
      case 'termin':
        return { label: t('appointments'), color: 'bg-violet-500' }
      case 'frist':
        return { label: t('deadlines'), color: 'bg-rose-500' }
      case 'wiedervorlage':
        return { label: t('followUps'), color: 'bg-amber-500' }
    }
  }

  const sourceLabel = (source: AccountSource): string => {
    switch (source) {
      case 'calendar':
        return t('sourceCalendar')
      case 'terminbuchung':
        return t('sourceBooking')
      case 'both':
        return t('sourceBoth')
    }
  }

  const statusMeta = (status: SyncStatus): { label: string; dot: string } => {
    switch (status) {
      case 'synced':
        return { label: t('statusSynced'), dot: 'bg-emerald-500' }
      case 'syncing':
        return { label: t('statusSyncing'), dot: 'bg-amber-500' }
      case 'error':
        return { label: t('statusError'), dot: 'bg-rose-500' }
    }
  }

  const levelLabel = (level: PermissionLevel): string => {
    switch (level) {
      case 'owner':
        return t('owner')
      case 'edit':
        return t('edit')
      case 'view':
        return t('view')
    }
  }

  const buildInitials = (
    first?: string | null,
    last?: string | null,
    fallback?: string | null
  ): string => {
    const initials = `${first?.[0] ?? ''}${last?.[0] ?? ''}`.trim()
    if (initials) {
      return initials.toUpperCase()
    }

    if (fallback) {
      return fallback.slice(0, 2).toUpperCase()
    }

    return '??'
  }

  const organisationUsers = computed<SettingsUser[]>(() => {
    const users = props.currentUser?.org?.users ?? []

    return users.map((user) => ({
      ...user,
      name: `${user.first_name} ${user.last_name}`.trim() || user.email,
      initials: buildInitials(user.first_name, user.last_name, user.email)
    }))
  })

  const currentSettingsUser = computed<SettingsUser | null>(() => {
    if (!props.currentUser?.uuid) {
      return null
    }

    const orgUser = organisationUsers.value.find((user) => user.uuid === props.currentUser?.uuid)
    return (
      orgUser ?? {
        uuid: props.currentUser.uuid,
        first_name: props.currentUser.first_name ?? '',
        last_name: props.currentUser.last_name ?? '',
        avatar: props.currentUser.avatar ?? null,
        name:
          `${props.currentUser.first_name ?? ''} ${props.currentUser.last_name ?? ''}`.trim() ||
          props.currentUser.email ||
          t('currentUser'),
        email: props.currentUser.email ?? '',
        initials: buildInitials(
          props.currentUser.first_name,
          props.currentUser.last_name,
          props.currentUser.email ?? ''
        )
      }
    )
  })

  const currentUserId = computed(() => currentSettingsUser.value?.uuid ?? '')

  const isCurrentUserAdmin = computed(() => {
    return Boolean(
      props.currentUser?.role === RoleEnum.ADMIN ||
        (props.currentUser as { is_superuser?: boolean } | null)?.is_superuser
    )
  })

  watch(
    isCurrentUserAdmin,
    (isAdmin) => {
      viewAs.value = isAdmin ? 'admin' : 'staff'
    },
    { immediate: true }
  )

  const inferProvider = (email: string, accountId: string): ProviderKey => {
    const source = `${email} ${accountId}`.toLowerCase()
    if (source.includes('gmail') || source.includes('google')) {
      return 'google'
    }
    if (source.includes('icloud') || source.includes('me.com') || source.includes('apple')) {
      return 'icloud'
    }
    if (source.includes('outlook') || source.includes('hotmail') || source.includes('live.com')) {
      return 'outlook'
    }
    if (source.includes('exchange')) {
      return 'exchange'
    }
    return 'office365'
  }

  const providerFromReadableName = (value?: string): ProviderKey | null => {
    if (value === 'Google') {
      return 'google'
    }
    if (value === 'Office 365') {
      return 'office365'
    }
    if (value === 'iCloud') {
      return 'icloud'
    }
    if (value === 'Outlook.com') {
      return 'outlook'
    }
    if (value === 'Exchange') {
      return 'exchange'
    }
    return null
  }

  const normalizePermissionLevel = (value?: string | null): PermissionLevel | null => {
    const normalized = value?.toUpperCase()
    if (normalized === 'OWNER') {
      return 'owner'
    }
    if (normalized === 'EDIT') {
      return 'edit'
    }
    if (normalized === 'VIEW') {
      return 'view'
    }
    return null
  }

  const userByUuid = (uuid?: string | null): SettingsUser | undefined =>
    organisationUsers.value.find((user) => user.uuid === uuid)

  const permissionLevelRank = (value?: string | null): number => {
    const normalized = normalizePermissionLevel(value)
    if (normalized === 'owner') {
      return 3
    }
    if (normalized === 'edit') {
      return 2
    }
    if (normalized === 'view') {
      return 1
    }
    return 0
  }

  const realAccounts = computed<ConnectedAccount[]>(() => {
    const settingsCalendars = props.calendars
    const groupedCalendars = new Map<string, CustomerCalendar[]>()

    for (const calendar of settingsCalendars) {
      if (calendar.deactivated || calendar.calendar_deleted) {
        continue
      }

      const groupKey = `${calendar.profile}:${calendar.email}`
      const existing = groupedCalendars.get(groupKey)
      if (existing) {
        existing.push(calendar)
        continue
      }

      groupedCalendars.set(groupKey, [calendar])
    }

    return Array.from(groupedCalendars.values()).map((group) => {
      const primaryCalendar =
        group.find((calendar) => calendar.calendar_primary) ??
        group.find((calendar) => calendar.name?.trim()) ??
        group[0]
      const editableCalendar =
        group.find((calendar) => !calendar.calendar_readonly) ?? primaryCalendar
      const ownerCalendar = group.find((calendar) => calendar.owner) ?? editableCalendar
      const highestPermissionCalendar = [...group].sort(
        (left, right) =>
          permissionLevelRank(right.permission_level) - permissionLevelRank(left.permission_level)
      )[0]
      const readableProvider = providerFromReadableName(primaryCalendar.readable_provider_name)
      const retryCalendar = group.find(
        (calendar) => calendar.profile_connected === false && calendar.profile_relink_url
      )

      return {
        id: `profile-${primaryCalendar.profile}`,
        accountUuid: editableCalendar.account?.uuid ?? primaryCalendar.account?.uuid,
        profileId: primaryCalendar.profile,
        calendarUuids: group
          .map((calendar) => calendar.uuid)
          .filter((uuid): uuid is string => Boolean(uuid)),
        provider:
          readableProvider ??
          inferProvider(primaryCalendar.email, primaryCalendar.account?.account_id ?? ''),
        providerInferred: readableProvider === null,
        email: primaryCalendar.email,
        source: 'calendar' as const,
        status: group.some((calendar) => calendar.profile_connected === false) ? 'error' : 'synced',
        lastSyncAt: null,
        syncedKinds: { termin: true, frist: true, wiedervorlage: true },
        bookableLeistungen: [],
        ownerId: ownerCalendar.owner || 'firm',
        calendarName:
          primaryCalendar.name?.trim() ||
          primaryCalendar.calendar_name?.trim() ||
          ownerCalendar.owner_email ||
          primaryCalendar.email,
        permissionLevel: highestPermissionCalendar?.permission_level || '',
        canManage: group.some((calendar) => !calendar.calendar_readonly),
        retryUrl: retryCalendar?.profile_relink_url ?? null,
        errorMessage: group.some((calendar) => calendar.profile_connected === false)
          ? t('syncNeedsAttention')
          : null
      }
    })
  })

  const allAccounts = computed<ConnectedAccount[]>(() => [
    ...realAccounts.value,
    ...syntheticAccounts.value
  ])

  const buildInitialPermissions = (account: ConnectedAccount): PermissionRow[] => {
    const rows: PermissionRow[] = []
    const ownerUser = userByUuid(account.ownerId === 'firm' ? null : account.ownerId)
    rows.push({
      userId: account.ownerId === 'firm' ? undefined : account.ownerId,
      email: ownerUser?.email || account.email,
      name: ownerUser?.name || account.calendarName,
      initials: ownerUser?.initials || buildInitials(undefined, undefined, account.calendarName),
      level: 'owner'
    })

    const currentUser = currentSettingsUser.value
    const currentUserLevel = normalizePermissionLevel(account.permissionLevel)
    if (
      currentUser &&
      currentUserLevel &&
      currentUser.uuid !== rows[0]?.userId &&
      !rows.some((row) => row.userId === currentUser.uuid)
    ) {
      rows.push({
        userId: currentUser.uuid,
        email: currentUser.email,
        name: currentUser.name,
        initials: currentUser.initials,
        level: currentUserLevel
      })
    }

    return rows
  }

  watch(
    allAccounts,
    (accounts) => {
      for (const account of accounts) {
        permissionsState.value[account.id] ??= buildInitialPermissions(account)
      }
    },
    { immediate: true }
  )

  const hasOwner = (accountId: string): boolean =>
    (permissionsState.value[accountId] ?? []).some((permission) => permission.level === 'owner')

  const isVisible = (accountId: string): boolean => {
    if (viewAs.value === 'admin') {
      return true
    }

    const account = allAccounts.value.find((entry) => entry.id === accountId)
    if (!account) {
      return false
    }

    if (account.ownerId === currentUserId.value) {
      return true
    }

    return (permissionsState.value[accountId] ?? []).some(
      (permission) => permission.userId === currentUserId.value
    )
  }

  const canManageCalendar = (accountId: string): boolean => {
    const account = allAccounts.value.find((entry) => entry.id === accountId)
    if (!account) {
      return false
    }

    if (viewAs.value === 'admin') {
      return account.canManage
    }

    return account.ownerId === currentUserId.value && account.canManage
  }

  const canManageSync = (accountId: string): boolean => canManageCalendar(accountId)

  const visibleAccounts = computed(() =>
    allAccounts.value.filter((account) => isVisible(account.id))
  )

  const visiblePermissions = computed<PermissionsWithMeta[]>(() =>
    visibleAccounts.value.map((account) => ({
      account,
      permissions: permissionsState.value[account.id] ?? [],
      canManage: canManageCalendar(account.id),
      ownerAssignable: viewAs.value === 'admin' && !hasOwner(account.id)
    }))
  )

  const formatRelative = (iso: string | null): string => {
    if (!iso) {
      return t('timeUnknown')
    }

    const date = new Date(iso)
    const diffMin = Math.round((Date.now() - date.getTime()) / 60000)
    const formatter = new Intl.RelativeTimeFormat(locale.value === 'de' ? 'de-DE' : 'en-US', {
      numeric: 'auto'
    })
    if (diffMin < 1) {
      return formatter.format(0, 'minute')
    }
    if (diffMin < 60) {
      return formatter.format(-diffMin, 'minute')
    }
    const diffH = Math.round(diffMin / 60)
    if (diffH < 24) {
      return formatter.format(-diffH, 'hour')
    }
    const diffD = Math.round(diffH / 24)
    return formatter.format(-diffD, 'day')
  }

  const addStep = ref<AddStep>('closed')
  const pendingProvider = ref<ProviderKey | null>(null)
  const addError = ref('')

  const openAddAccount = (): void => {
    addError.value = ''
    pendingProvider.value = null
    addStep.value = 'choose'
  }

  const openSyncAddAccount = (): void => {
    activeTab.value = 'sync'
    openAddAccount()
  }

  const cancelAdd = (): void => {
    addStep.value = 'closed'
    pendingProvider.value = null
    addError.value = ''
  }

  const appointmentsAuthorizeUrl = computed(() => {
    const returnTo = new URL(window.location.href)
    if (props.restoreModalOnReturn) {
      returnTo.searchParams.set('calendarSettings', '1')
    } else {
      returnTo.searchParams.delete('calendarSettings')
    }
    returnTo.searchParams.delete('calendarAuthError')

    return `${window.location.origin}/appointments/authorize/?return_to=${encodeURIComponent(
      `${returnTo.pathname}${returnTo.search}${returnTo.hash}`
    )}`
  })

  const chooseProvider = (provider: ProviderKey): void => {
    pendingProvider.value = provider
    addError.value = ''
    addStep.value = 'confirm'
  }

  const continueToProvider = (): void => {
    if (!pendingProvider.value) {
      return
    }

    window.location.assign(appointmentsAuthorizeUrl.value)
  }

  const retryAuth = (): void => {
    if (!pendingProvider.value) {
      openAddAccount()
      return
    }

    chooseProvider(pendingProvider.value)
  }

  const backToProviderChoice = (): void => {
    addError.value = ''
    pendingProvider.value = null
    addStep.value = 'choose'
  }

  const isKindSyncedForAccount = (account: ConnectedAccount, kind: EntryKind): boolean => {
    if (account.calendarUuids.length === 0) {
      return account.syncedKinds[kind]
    }

    return account.calendarUuids.every((uuid) => props.calendarKindChecks[uuid]?.[kind] !== false)
  }

  const toggleKindSync = (account: ConnectedAccount, kind: EntryKind): void => {
    emit('set-account-kind-sync', {
      calendarUuids: account.calendarUuids,
      kind,
      checked: !isKindSyncedForAccount(account, kind)
    })
  }

  const retryAccountSync = (account: ConnectedAccount): void => {
    if (!account.retryUrl) {
      return
    }

    window.location.assign(account.retryUrl)
  }

  const confirmDisconnect = ref<string | null>(null)
  const disconnectWarningLeistungen = ref<string[]>([])
  const disconnectWarningLoading = ref(false)
  const disconnectWarningLoadFailed = ref(false)
  let disconnectWarningRequestId = 0

  const disconnectingAccount = computed(() => {
    if (!confirmDisconnect.value) {
      return null
    }

    return allAccounts.value.find((account) => account.id === confirmDisconnect.value) ?? null
  })

  const disconnectingNow = computed(() =>
    Boolean(
      disconnectingAccount.value?.accountUuid &&
        props.disconnectingAccountUuid === disconnectingAccount.value.accountUuid
    )
  )

  const closeDisconnectWarning = (): void => {
    if (disconnectingNow.value) {
      return
    }

    confirmDisconnect.value = null
    disconnectWarningLeistungen.value = []
    disconnectWarningLoading.value = false
    disconnectWarningLoadFailed.value = false
  }

  const loadAffectedLeistungen = async (account: ConnectedAccount): Promise<void> => {
    if (!account.accountUuid || account.localOnly) {
      disconnectWarningLeistungen.value = account.bookableLeistungen
      disconnectWarningLoadFailed.value = false
      disconnectWarningLoading.value = false
      return
    }

    const requestId = ++disconnectWarningRequestId
    disconnectWarningLoading.value = true
    disconnectWarningLoadFailed.value = false

    try {
      const data = await AccountsService.accountsAffectedLeistungenRetrieve({
        uuid: account.accountUuid
      })
      if (requestId !== disconnectWarningRequestId || confirmDisconnect.value !== account.id) {
        return
      }

      disconnectWarningLeistungen.value = data.affected_leistungen ?? []
    } catch (error) {
      console.error('[calendar] loadAffectedLeistungen failed', error)
      if (requestId !== disconnectWarningRequestId || confirmDisconnect.value !== account.id) {
        return
      }

      disconnectWarningLoadFailed.value = true
      disconnectWarningLeistungen.value = []
      genericErrorToast(t('affectedServicesLoadError'))
    } finally {
      if (requestId === disconnectWarningRequestId && confirmDisconnect.value === account.id) {
        disconnectWarningLoading.value = false
      }
    }
  }

  const openDisconnectWarning = (accountId: string): void => {
    const account = allAccounts.value.find((entry) => entry.id === accountId)
    if (!account) {
      return
    }

    confirmDisconnect.value = accountId
    disconnectWarningLeistungen.value = account.bookableLeistungen
    disconnectWarningLoadFailed.value = false
    disconnectWarningLoading.value = false
    void loadAffectedLeistungen(account)
  }

  const reallyDisconnect = (): void => {
    const account = disconnectingAccount.value
    if (!account || disconnectWarningLoadFailed.value) {
      return
    }

    if (account.localOnly || !account.accountUuid) {
      syntheticAccounts.value = syntheticAccounts.value.filter((entry) => entry.id !== account.id)
      closeDisconnectWarning()
      return
    }

    emit('disconnect-account', account.accountUuid)
    closeDisconnectWarning()
  }

  const PERMISSION_LEVELS: PermissionLevel[] = ['owner', 'edit', 'view']
  const inviteOpenFor = ref<string | null>(null)
  const inviteQuery = ref('')
  const inviteLevel = ref<PermissionLevel>('edit')

  const filteredInviteUsers = computed<SettingsUser[]>(() => {
    if (!inviteOpenFor.value) {
      return []
    }

    const taken = new Set(
      (permissionsState.value[inviteOpenFor.value] ?? []).map((permission) => permission.userId)
    )
    const pool = organisationUsers.value.filter((user) => !taken.has(user.uuid))
    const query = inviteQuery.value.trim().toLowerCase()

    if (!query) {
      return pool
    }

    return pool.filter(
      (user) =>
        user.name.toLowerCase().includes(query) || user.initials.toLowerCase().includes(query)
    )
  })

  const openInvite = (accountId: string): void => {
    inviteOpenFor.value = accountId
    inviteQuery.value = ''
    inviteLevel.value = 'edit'
  }

  const closeInvite = (): void => {
    inviteOpenFor.value = null
    inviteQuery.value = ''
  }

  const addUserToCalendar = (accountId: string, user: SettingsUser): void => {
    permissionsState.value[accountId] ??= []
    if (permissionsState.value[accountId].some((permission) => permission.userId === user.uuid)) {
      return
    }

    permissionsState.value[accountId].push({
      userId: user.uuid,
      name: user.name,
      email: user.email,
      initials: user.initials,
      level: inviteLevel.value
    })
    closeInvite()
  }

  const isEmailLike = (value: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)

  const inviteByEmail = (accountId: string): void => {
    const email = inviteQuery.value.trim()
    if (!isEmailLike(email)) {
      return
    }

    permissionsState.value[accountId] ??= []
    permissionsState.value[accountId].push({
      email,
      name: email,
      initials: email.slice(0, 2).toUpperCase(),
      level: inviteLevel.value
    })
    closeInvite()
  }

  const removePermission = (accountId: string, index: number): void => {
    const permissions = permissionsState.value[accountId]
    if (!permissions || permissions[index]?.level === 'owner') {
      return
    }

    permissions.splice(index, 1)
  }

  const setPermissionLevel = (accountId: string, index: number, level: PermissionLevel): void => {
    const permissions = permissionsState.value[accountId]
    const row = permissions?.[index]
    if (!row) {
      return
    }

    if (row.level === 'owner') {
      return
    }

    if (level === 'owner') {
      if (viewAs.value !== 'admin' || hasOwner(accountId)) {
        return
      }
      row.level = 'owner'
      return
    }

    row.level = level
  }
</script>

<template>
  <div class="flex w-full flex-col">
    <div class="grid grid-cols-3 items-center px-5 pb-2 pt-4">
      <div></div>
      <div class="flex justify-center">
        <div class="bg-surface inline-flex rounded-full p-0.5">
          <button
            v-for="tab in tabs"
            :key="tab.id"
            type="button"
            :class="[
              'focus:outline-hidden rounded-full px-3.5 py-1 text-xs transition-colors',
              activeTab === tab.id
                ? 'bg-primary font-medium text-primary-foreground shadow-sm'
                : 'text-muted-foreground-1 hover:text-foreground'
            ]"
            @click="activeTab = tab.id"
          >
            {{ tab.label }}
          </button>
        </div>
      </div>
      <div class="flex justify-end pe-10">
        <label
          class="text-muted-foreground-1 inline-flex cursor-pointer items-center gap-x-1.5 text-[11px]"
          :title="t('viewModeTitle')"
        >
          <span>{{ t('viewLabel') }}</span>
          <select
            v-model="viewAs"
            class="border-layer-line bg-layer focus:outline-hidden rounded-md border px-1.5 py-0.5 text-[11px]
              text-foreground focus:ring-2 focus:ring-primary/30"
          >
            <option
              value="admin"
              :disabled="!isCurrentUserAdmin"
            >
              {{ t('admin') }}
            </option>
            <option value="staff">{{ t('staff') }}</option>
          </select>
        </label>
      </div>
    </div>

    <div
      v-if="activeTab === 'sync'"
      class="grow overflow-y-auto px-5 py-4"
    >
      <div
        v-if="addStep === 'choose'"
        class="space-y-4"
      >
        <div class="flex items-center gap-2">
          <button
            type="button"
            class="text-muted-foreground-1 hover:bg-muted-hover focus:outline-hidden inline-flex size-7 items-center
              justify-center rounded-md"
            :aria-label="t('back')"
            @click="cancelAdd"
          >
            <svg
              class="size-4 shrink-0"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="m15 18-6-6 6-6" />
            </svg>
          </button>
          <div>
            <h3 class="text-sm font-semibold text-foreground">{{ t('addCalendarAccount') }}</h3>
            <p class="text-muted-foreground-1 text-[11px]">
              {{ t('chooseProviderDescription') }}
            </p>
          </div>
        </div>

        <div class="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
          <button
            v-for="provider in providerKeys"
            :key="provider"
            type="button"
            class="border-card-line hover:bg-card-hover focus:outline-hidden flex items-center gap-3 rounded-lg border
              bg-card p-3 text-start transition-colors hover:border-primary/50 focus:ring-2 focus:ring-primary/30"
            @click="chooseProvider(provider)"
          >
            <CalendarProviderLogo
              :provider="provider"
              :size="32"
              class="shrink-0"
            />
            <div class="min-w-0 grow">
              <p class="text-sm font-medium text-foreground">{{ PROVIDER_META[provider].label }}</p>
              <p class="text-muted-foreground-1 truncate text-[11px]">
                {{ t('includesService', { service: PROVIDER_META[provider].videoService }) }}
              </p>
            </div>
            <svg
              class="text-muted-foreground-1 size-4 shrink-0"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="m9 18 6-6-6-6" />
            </svg>
          </button>
        </div>

        <p class="text-muted-foreground-1 text-[11px]">
          {{ t('requiredPermissionsHelp') }}
        </p>
      </div>

      <div
        v-else-if="addStep === 'confirm' && pendingProvider"
        class="flex justify-center py-6"
      >
        <div class="border-card-line w-full max-w-md rounded-xl border bg-card p-6 shadow-sm">
          <div class="flex flex-col items-center text-center">
            <CalendarProviderLogo
              :provider="pendingProvider"
              :size="44"
            />
            <h3 class="mt-4 text-base font-semibold text-foreground">
              {{ t('redirectToProvider', { provider: PROVIDER_META[pendingProvider].label }) }}
            </h3>
            <p class="text-muted-foreground-1 mt-2 text-sm">
              {{
                t('redirectToProviderDescription', {
                  provider: PROVIDER_META[pendingProvider].label
                })
              }}
            </p>
          </div>

          <div class="border-line-2 bg-surface mt-5 rounded-lg border p-3 text-xs text-foreground">
            <p class="font-medium">{{ t('accessRequestTitle') }}</p>
            <ul class="text-muted-foreground-1 mt-2 space-y-1">
              <li>{{ t('accessCalendarPermission') }}</li>
              <li>
                {{
                  t('accessVideoService', {
                    service: PROVIDER_META[pendingProvider].videoService
                  })
                }}
              </li>
              <li>{{ t('accessAccountEmail') }}</li>
            </ul>
          </div>

          <p class="text-muted-foreground-1 mt-4 text-center text-xs">
            {{
              t('providerAuthHappensExternally', {
                provider: PROVIDER_META[pendingProvider].label
              })
            }}
          </p>

          <div class="mt-6 flex items-center justify-between gap-3">
            <button
              type="button"
              class="text-muted-foreground-2 hover:bg-muted-hover focus:outline-hidden rounded-md px-3 py-2 text-xs"
              @click="backToProviderChoice"
            >
              {{ t('backToSelection') }}
            </button>
            <button
              type="button"
              class="focus:outline-hidden rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground
                hover:bg-primary-hover"
              @click="continueToProvider"
            >
              {{ t('continue') }}
            </button>
          </div>
        </div>
      </div>

      <div
        v-else-if="addStep === 'error'"
        class="flex flex-col items-center justify-center py-10 text-center"
      >
        <span
          class="mb-3 inline-flex size-12 items-center justify-center rounded-full bg-rose-500/15 text-rose-500"
        >
          <svg
            class="size-6 shrink-0"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <circle
              cx="12"
              cy="12"
              r="10"
            />
            <line
              x1="12"
              y1="8"
              x2="12"
              y2="12"
            />
            <line
              x1="12"
              y1="16"
              x2="12.01"
              y2="16"
            />
          </svg>
        </span>
        <h3 class="text-sm font-semibold text-foreground">{{ t('connectionFailed') }}</h3>
        <p class="text-muted-foreground-1 mt-1 max-w-sm text-xs">{{ addError }}</p>
        <div class="mt-5 flex items-center gap-2">
          <button
            type="button"
            class="text-muted-foreground-2 hover:bg-muted-hover focus:outline-hidden rounded-md px-3 py-1.5 text-xs"
            @click="backToProviderChoice"
          >
            {{ t('backToSelection') }}
          </button>
          <button
            type="button"
            class="focus:outline-hidden rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground
              hover:bg-primary-hover"
            @click="retryAuth"
          >
            {{ t('retry') }}
          </button>
        </div>
      </div>

      <template v-else>
        <div
          v-if="props.accountsLoading"
          class="flex flex-col items-center justify-center gap-4 py-16 text-center"
        >
          <div
            class="bg-surface ring-layer-line flex size-14 items-center justify-center rounded-full ring-1"
          >
            <span
              class="size-5 animate-spin rounded-full border-2 border-primary border-t-transparent"
            ></span>
          </div>
          <p class="text-muted-foreground-1 text-sm">{{ t('loadingAccounts') }}</p>
        </div>

        <div
          v-else-if="allAccounts.length === 0"
          class="flex flex-col items-center justify-center gap-4 py-16 text-center"
        >
          <div
            class="bg-surface ring-layer-line flex size-14 items-center justify-center rounded-full ring-1"
          >
            <svg
              class="text-muted-foreground-1 size-6"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <rect
                width="18"
                height="18"
                x="3"
                y="4"
                rx="2"
              />
              <path d="M16 2v4" />
              <path d="M8 2v4" />
              <path d="M3 10h18" />
              <path d="m14 14-4 4" />
              <path d="m10 14 4 4" />
            </svg>
          </div>
          <div>
            <p class="text-sm font-medium text-foreground">{{ t('noAccountsSynced') }}</p>
            <p class="text-muted-foreground-1 mt-1 text-xs">
              {{ t('syncEmptyDescription') }}
            </p>
          </div>
          <button
            type="button"
            class="border-primary-line focus:outline-hidden inline-flex items-center gap-x-1.5 rounded-md border
              bg-primary px-3 py-1.5 text-xs text-primary-foreground hover:bg-primary-hover"
            @click="openAddAccount"
          >
            <svg
              class="size-3 shrink-0"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M5 12h14" />
              <path d="M12 5v14" />
            </svg>
            {{ t('syncCalendar') }}
          </button>
        </div>

        <template v-else>
          <div
            class="mb-4 flex items-start gap-2 rounded-lg border border-primary/20 bg-primary/5 p-3 text-xs
              text-foreground"
          >
            <svg
              class="mt-0.5 size-4 shrink-0 text-primary"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8" />
              <path d="M21 3v5h-5" />
            </svg>
            <div>
              <p class="font-medium">{{ t('accountsApplyHeadline') }}</p>
              <p class="text-muted-foreground-1 mt-0.5">
                {{ t('accountsApplyDescription') }}
              </p>
            </div>
          </div>

          <div class="mb-3 flex items-center justify-between">
            <h3 class="text-sm font-semibold text-foreground">{{ t('connectedAccounts') }}</h3>
            <button
              type="button"
              class="focus:outline-hidden inline-flex items-center gap-x-1.5 rounded-lg bg-primary px-2.5 py-1.5 text-xs
                text-primary-foreground hover:bg-primary-hover"
              @click="openAddAccount"
            >
              <svg
                class="size-3.5 shrink-0"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M5 12h14" />
                <path d="M12 5v14" />
              </svg>
              {{ t('addAccount') }}
            </button>
          </div>

          <div class="space-y-3">
            <div
              v-for="account in visibleAccounts"
              :key="account.id"
              class="border-card-line rounded-lg border bg-card"
            >
              <div class="flex items-start gap-3 p-3">
                <CalendarProviderLogo
                  :provider="account.provider"
                  :size="36"
                  class="shrink-0"
                />
                <div class="min-w-0 grow">
                  <div class="flex flex-wrap items-center gap-2">
                    <p class="truncate text-sm font-medium text-foreground">
                      {{ account.calendarName }}
                    </p>
                    <span class="text-muted-foreground-1 text-[11px]">· {{ account.email }}</span>
                  </div>
                  <div
                    class="text-muted-foreground-1 mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]"
                  >
                    <span class="inline-flex items-center gap-x-1">
                      <span
                        :class="['size-1.5 rounded-full', statusMeta(account.status).dot]"
                      ></span>
                      {{ statusMeta(account.status).label }}
                      <template v-if="account.lastSyncAt">
                        · {{ formatRelative(account.lastSyncAt) }}
                      </template>
                    </span>
                    <span
                      class="inline-flex items-center gap-x-1"
                      :title="
                        t('videoConferencesVia', {
                          service: PROVIDER_META[account.provider].videoService
                        })
                      "
                    >
                      <svg
                        class="size-3 shrink-0"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      >
                        <path d="m22 8-6 4 6 4V8Z" />
                        <rect
                          width="14"
                          height="12"
                          x="2"
                          y="6"
                          rx="2"
                        />
                      </svg>
                      {{ PROVIDER_META[account.provider].videoService }}
                    </span>
                    <span class="text-muted-foreground-1/70">
                      {{ sourceLabel(account.source) }}
                    </span>
                  </div>
                  <p
                    v-if="account.errorMessage"
                    class="mt-1 text-[11px] text-rose-500"
                  >
                    {{ account.errorMessage }}
                  </p>
                </div>
                <div class="flex shrink-0 items-start gap-2">
                  <button
                    v-if="account.retryUrl && canManageSync(account.id)"
                    type="button"
                    class="focus:outline-hidden rounded-md px-2 py-1 text-[11px] text-primary hover:bg-primary/10"
                    @click="retryAccountSync(account)"
                  >
                    {{ t('retryConnection') }}
                  </button>
                  <button
                    v-if="canManageSync(account.id)"
                    type="button"
                    class="focus:outline-hidden rounded-md px-2 py-1 text-[11px] text-rose-500 hover:bg-rose-500/10
                      disabled:cursor-not-allowed disabled:opacity-60"
                    :disabled="props.disconnectingAccountUuid === account.accountUuid"
                    @click="openDisconnectWarning(account.id)"
                  >
                    {{
                      props.disconnectingAccountUuid === account.accountUuid
                        ? '...'
                        : t('disconnectConnection')
                    }}
                  </button>
                  <span
                    v-else
                    class="bg-surface text-muted-foreground-1 inline-flex items-center gap-x-1 rounded-md px-1.5 py-0.5
                      text-[10px]"
                    :title="t('readOnlyDisconnectHint')"
                  >
                    <svg
                      class="size-3 shrink-0"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <rect
                        width="18"
                        height="11"
                        x="3"
                        y="11"
                        rx="2"
                      />
                      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                    </svg>
                    {{ t('readOnlyAccess') }}
                  </span>
                </div>
              </div>

              <div class="border-card-line border-t">
                <div
                  class="text-muted-foreground-1 flex items-center justify-between px-3 py-1.5 text-[10px] uppercase
                    tracking-wide"
                >
                  <span>{{ t('entryKind') }}</span>
                  <span>{{ t('syncColumn') }}</span>
                </div>
                <ul>
                  <li
                    v-for="kind in ENTRY_KINDS"
                    :key="kind"
                    class="border-card-line flex items-center justify-between border-t px-3 py-2 text-xs text-foreground"
                  >
                    <div class="flex min-w-0 items-center gap-2">
                      <span
                        :class="['size-2.5 shrink-0 rounded-full', entryKindMeta(kind).color]"
                      ></span>
                      <span class="truncate">{{ entryKindMeta(kind).label }}</span>
                    </div>
                    <label
                      :class="[
                        'relative inline-flex items-center',
                        canManageSync(account.id)
                          ? 'cursor-pointer'
                          : 'cursor-not-allowed opacity-60'
                      ]"
                    >
                      <input
                        type="checkbox"
                        class="peer sr-only"
                        :checked="isKindSyncedForAccount(account, kind)"
                        :disabled="!canManageSync(account.id)"
                        @change="toggleKindSync(account, kind)"
                      />
                      <span
                        class="border-line-2 bg-surface h-5 w-9 rounded-full border transition-colors duration-200
                          peer-checked:border-primary peer-checked:bg-primary"
                      ></span>
                      <span
                        class="absolute start-0.5 top-0.5 size-4 rounded-full bg-white shadow-[0_1px_2px_rgba(0,0,0,0.16)] ring-1
                          ring-black/5 transition-transform duration-200 peer-checked:translate-x-4"
                      ></span>
                    </label>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </template>
      </template>
    </div>

    <div
      v-else
      class="grow overflow-y-auto px-5 py-4"
    >
      <div
        v-if="props.accountsLoading"
        class="flex flex-col items-center justify-center gap-4 py-16 text-center"
      >
        <div
          class="bg-surface ring-layer-line flex size-14 items-center justify-center rounded-full ring-1"
        >
          <span
            class="size-5 animate-spin rounded-full border-2 border-primary border-t-transparent"
          ></span>
        </div>
        <p class="text-muted-foreground-1 text-sm">{{ t('loadingAccounts') }}</p>
      </div>

      <div
        v-else-if="allAccounts.length === 0"
        class="flex flex-col items-center justify-center gap-4 py-16 text-center"
      >
        <div
          class="bg-surface ring-layer-line flex size-14 items-center justify-center rounded-full ring-1"
        >
          <svg
            class="text-muted-foreground-1 size-6"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <rect
              width="18"
              height="18"
              x="3"
              y="4"
              rx="2"
            />
            <path d="M16 2v4" />
            <path d="M8 2v4" />
            <path d="M3 10h18" />
            <path d="m14 14-4 4" />
            <path d="m10 14 4 4" />
          </svg>
        </div>
        <div>
          <p class="text-sm font-medium text-foreground">{{ t('noAccountsSynced') }}</p>
          <p class="text-muted-foreground-1 mt-1 text-xs">
            {{ t('permissionsEmptyDescription') }}
          </p>
        </div>
        <button
          type="button"
          class="border-primary-line focus:outline-hidden inline-flex items-center gap-x-1.5 rounded-md border
            bg-primary px-3 py-1.5 text-xs text-primary-foreground hover:bg-primary-hover"
          @click="openSyncAddAccount"
        >
          <svg
            class="size-3 shrink-0"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M5 12h14" />
            <path d="M12 5v14" />
          </svg>
          {{ t('syncCalendar') }}
        </button>
      </div>

      <div v-else>
        <div
          v-if="viewAs === 'staff'"
          class="mb-4 flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-xs
            text-foreground"
        >
          <svg
            class="mt-0.5 size-4 shrink-0 text-amber-500"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <rect
              width="18"
              height="11"
              x="3"
              y="11"
              rx="2"
            />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
          <div>
            <p class="font-medium">{{ t('limitedManagementTitle') }}</p>
            <p class="text-muted-foreground-1 mt-0.5">
              {{ t('limitedManagementDescription') }}
            </p>
          </div>
        </div>

        <div class="space-y-4">
          <section
            v-for="entry in visiblePermissions"
            :key="entry.account.id"
            class="border-card-line rounded-lg border bg-card"
          >
            <header class="border-card-line flex items-center justify-between border-b px-3 py-2.5">
              <div class="min-w-0">
                <p class="truncate text-sm font-medium text-foreground">
                  {{ entry.account.calendarName }}
                </p>
                <p class="text-muted-foreground-1 text-[11px]">
                  {{ entry.account.email }} · {{ PROVIDER_META[entry.account.provider].label }}
                </p>
              </div>
              <span
                v-if="!entry.canManage"
                class="bg-surface text-muted-foreground-1 inline-flex items-center gap-x-1 rounded-md px-1.5 py-0.5
                  text-[10px]"
              >
                <svg
                  class="size-3 shrink-0"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <rect
                    width="18"
                    height="11"
                    x="3"
                    y="11"
                    rx="2"
                  />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
                {{ t('readOnlyAccess') }}
              </span>
            </header>

            <ul class="divide-card-line divide-y">
              <li
                v-for="(permission, index) in entry.permissions"
                :key="(permission.userId ?? permission.email ?? permission.name) + '-' + index"
                class="flex items-center gap-3 px-3 py-2"
              >
                <span
                  class="bg-surface inline-flex size-7 shrink-0 items-center justify-center rounded-full text-[11px]
                    uppercase text-foreground"
                >
                  {{ permission.initials }}
                </span>
                <div class="min-w-0 grow">
                  <p class="truncate text-sm text-foreground">{{ permission.name }}</p>
                  <p
                    v-if="permission.email"
                    class="text-muted-foreground-1 truncate text-[11px]"
                  >
                    {{ permission.email }}
                  </p>
                </div>
                <select
                  :value="permission.level"
                  :disabled="!entry.canManage || permission.level === 'owner'"
                  class="border-layer-line bg-layer focus:outline-hidden w-32 rounded-md border px-2 py-1 text-xs
                    text-foreground focus:ring-2 focus:ring-primary/30 disabled:opacity-60"
                  @change="
                    setPermissionLevel(
                      entry.account.id,
                      index,
                      ($event.target as HTMLSelectElement).value as PermissionLevel
                    )
                  "
                >
                  <option
                    v-for="level in PERMISSION_LEVELS"
                    v-show="
                      level !== 'owner' || permission.level === 'owner' || entry.ownerAssignable
                    "
                    :key="level"
                    :value="level"
                  >
                    {{ levelLabel(level) }}
                  </option>
                </select>
                <button
                  type="button"
                  :disabled="!entry.canManage || permission.level === 'owner'"
                  class="text-muted-foreground-1 hover:bg-muted-hover focus:outline-hidden inline-flex size-7 shrink-0
                    items-center justify-center rounded-md disabled:opacity-40 disabled:hover:bg-transparent"
                  :aria-label="t('removeAccess')"
                  @click="removePermission(entry.account.id, index)"
                >
                  <svg
                    class="size-3.5 shrink-0"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path d="M3 6h18" />
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
                    <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                  </svg>
                </button>
              </li>
            </ul>

            <footer
              v-if="entry.canManage"
              class="border-card-line border-t px-3 py-2"
            >
              <button
                v-if="inviteOpenFor !== entry.account.id"
                type="button"
                class="focus:outline-hidden text-xs text-primary hover:underline"
                @click="openInvite(entry.account.id)"
              >
                {{ t('invitePerson') }}
              </button>
              <div
                v-else
                class="space-y-2"
              >
                <div class="flex items-center gap-2">
                  <input
                    v-model="inviteQuery"
                    type="text"
                    :placeholder="t('nameOrEmailPlaceholder')"
                    class="border-layer-line bg-layer placeholder:text-muted-foreground-1 focus:outline-hidden grow rounded-md
                      border px-2.5 py-1.5 text-xs text-foreground focus:ring-2 focus:ring-primary/30"
                  />
                  <select
                    v-model="inviteLevel"
                    class="border-layer-line bg-layer focus:outline-hidden rounded-md border px-2 py-1.5 text-xs
                      text-foreground focus:ring-2 focus:ring-primary/30"
                  >
                    <option value="edit">{{ levelLabel('edit') }}</option>
                    <option value="view">{{ levelLabel('view') }}</option>
                  </select>
                  <button
                    type="button"
                    class="text-muted-foreground-1 hover:bg-muted-hover focus:outline-hidden rounded-md px-2 py-1.5 text-xs"
                    @click="closeInvite"
                  >
                    {{ t('cancel') }}
                  </button>
                </div>
                <div
                  v-if="filteredInviteUsers.length > 0 || isEmailLike(inviteQuery.trim())"
                  class="border-dropdown-line rounded-md border p-1"
                >
                  <button
                    v-for="user in filteredInviteUsers"
                    :key="user.uuid"
                    type="button"
                    class="hover:bg-dropdown-item-hover focus:outline-hidden flex w-full items-center gap-x-2 rounded-md px-2
                      py-1.5 text-start text-xs text-foreground"
                    @click="addUserToCalendar(entry.account.id, user)"
                  >
                    <span
                      class="bg-surface inline-flex size-5 items-center justify-center rounded-full text-[10px] uppercase"
                    >
                      {{ user.initials }}
                    </span>
                    <span class="grow">{{ user.name }}</span>
                  </button>
                  <button
                    v-if="isEmailLike(inviteQuery.trim())"
                    type="button"
                    class="hover:bg-dropdown-item-hover focus:outline-hidden flex w-full items-center gap-x-2 rounded-md px-2
                      py-1.5 text-start text-xs text-foreground"
                    @click="inviteByEmail(entry.account.id)"
                  >
                    <svg
                      class="size-3.5 shrink-0"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <rect
                        width="20"
                        height="16"
                        x="2"
                        y="4"
                        rx="2"
                      />
                      <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                    </svg>
                    <span class="grow">{{ t('inviteEmail', { email: inviteQuery.trim() }) }}</span>
                  </button>
                </div>
              </div>
            </footer>
          </section>
        </div>
      </div>
    </div>

    <DisconnectWarningModal
      :open="Boolean(confirmDisconnect)"
      :account-email="disconnectingAccount?.email ?? ''"
      :affected-leistungen="disconnectWarningLeistungen"
      :loading="disconnectWarningLoading"
      :load-failed="disconnectWarningLoadFailed"
      :disconnecting="disconnectingNow"
      @close="closeDisconnectWarning"
      @confirm="reallyDisconnect"
    />
  </div>
</template>
